import React, { useEffect } from 'react'
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Wordmark } from '../design-system/atoms/Wordmark'
import { usePageContentWidth } from '../contexts/PageContentWidthContext'
import { useAuthUser, useAuthStatus } from '../contexts/AuthContext'
import { RateReview, MenuBook, TrendingUp } from '@mui/icons-material'
import { getIntendedRoute } from '../features/auth/utils/routePreservation'

const LandingPage: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const { setPageContentWidth } = usePageContentWidth()
    const user = useAuthUser()
    const { status, hydrated } = useAuthStatus()

    // Redirect to dashboard if user is logged in
    // Only redirect if:
    // 1. User is authenticated AND
    // 2. Current path is '/' AND
    // 3. No preserved route exists (user actually landed on root, not preserving from deep link)
    useEffect(() => {
        if (hydrated && user && status === 'signedIn') {
            const preservedRoute = getIntendedRoute()
            const isOnRootPath = location.pathname === '/'
            // Only redirect if on root path and no preserved route
            if (isOnRootPath && !preservedRoute) {
                void navigate('/dashboard', { replace: true })
            }
        }
    }, [user, status, hydrated, navigate, location])

    // Set page content width to 'lg' on mount
    useEffect(() => {
        setPageContentWidth('lg')
        return () => {
            setPageContentWidth(null)
        }
    }, [setPageContentWidth])

    const features = [
        {
            icon: <RateReview sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: t('landing:features.feedback.title'),
            description: t('landing:features.feedback.description'),
        },
        {
            icon: <MenuBook sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: t('landing:features.authenticText.title'),
            description: t('landing:features.authenticText.description'),
        },
        {
            icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: t('landing:features.audience.title'),
            description: t('landing:features.audience.description'),
        },
    ]

    return (
        <Container component="main" maxWidth="lg" sx={{ mt: 4 }}>
            {/* Hero Section */}
            <Box
                sx={{
                    pt: 8,
                    pb: 4,
                    px: 4,
                    textAlign: 'center',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.brand.primary} 0%, ${theme.palette.brand.tintedText} 100%)`,
                    borderRadius: 2,
                    color: 'white',
                    mb: 4,
                }}
            >
                <Typography variant="h1" component="h1" gutterBottom>
                    {t('landing:hero.title')}
                </Typography>
                <Typography variant="h5" component="p" sx={{ mb: 4, opacity: 0.9 }}>
                    {t('landing:hero.subtitle')}
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => void navigate('/register')}
                    sx={{
                        bgcolor: 'white',
                        // primary.main coral (#D85A30) on white is ~3.9:1 and
                        // fails WCAG AA. brand.tintedText (#993C1D) is ~6.9:1 and
                        // is theme-independent, matching the fixed white button.
                        color: (theme) => theme.palette.brand.tintedText,
                        '&:hover': {
                            bgcolor: 'grey.100',
                        },
                    }}
                >
                    {t('landing:hero.getStarted')}
                </Button>
            </Box>
            {/* Features Section */}
            <Box>
                <Typography
                    variant="h2"
                    component="h2"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        mb: 4,
                    }}
                >
                    <Trans
                        i18nKey="landing:features.title"
                        components={{ wordmark: <Wordmark /> }}
                    />
                </Typography>
                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid size={{ xs: 12, md: 4 }} key={index}>
                            <Card sx={{ height: '100%', textAlign: 'center' }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                                    <Typography variant="h5" component="h3" gutterBottom>
                                        {feature.title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Container>
    )
}

export default LandingPage
